import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  TextInputField,
  NumberInputField,
  SelectField,
  TextareaField,
  FormSubmitButton,
} from "@/components/forms/FormComponents";
import {
  createExpenseSchema,
  updateExpenseSchema,
  type CreateExpenseData,
  type UpdateExpenseData,
} from "@/lib/form-schemas";
import { toastHelpers } from "@/lib/toast-helpers";
import apiClient from "@/api/client";
import type { Expense } from "@/types";
import { X } from "lucide-react";

interface ExpenseFormProps {
  expense?: Expense;
  onSuccess?: () => void;
  onCancel?: () => void;
  mode?: "create" | "edit";
}

export function ExpenseForm({
  expense,
  onSuccess,
  onCancel,
  mode = "create",
}: ExpenseFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEditing = mode === "edit" && expense;

  const [previewUrl, setPreviewUrl] = useState<string>(
    expense?.receipt_url || ""
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  const categoryOptions = [
    {
      value: "utilities",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Utilities"),
    },
    {
      value: "rent",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Rent"),
    },
    {
      value: "salaries",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Salaries"),
    },
    {
      value: "supplies",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Supplies"),
    },
    {
      value: "maintenance",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Maintenance"),
    },
    {
      value: "marketing",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Marketing"),
    },
    {
      value: "transportation",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Transportation"),
    },
    {
      value: "equipment",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Equipment"),
    },
    {
      value: "food_ingredients",
      label: t(
        "AdminExpensesManagement.ExpenseForm.Categories.FoodIngredients"
      ),
    },
    {
      value: "other",
      label: t("AdminExpensesManagement.ExpenseForm.Categories.Other"),
    },
  ];

  const schema = isEditing ? updateExpenseSchema : createExpenseSchema;
  const defaultValues = isEditing
    ? {
        id: expense.id,
        title: expense.title,
        description: expense.description,
        amount: expense.amount,
        category: expense.category as any,
        expense_date: expense.expense_date.split("T")[0], // Format for date input
        receipt_url: expense.receipt_url || "",
        notes: expense.notes || "",
      }
    : {
        title: "",
        description: "",
        amount: 0,
        category: "other" as const,
        expense_date: new Date().toISOString().split("T")[0],
        receipt_url: "",
        notes: "",
      };

  const form = useForm<CreateExpenseData | UpdateExpenseData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const createMutation = useMutation({
    mutationFn: (data: CreateExpenseData) => apiClient.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toastHelpers.apiSuccess(
        t("AdminExpensesManagement.ExpenseForm.Toast.CreateTitle"),
        t("AdminExpensesManagement.ExpenseForm.Toast.CreateSuccess")
      );
      form.reset();
      onSuccess?.();
    },
    onError: (error) => {
      toastHelpers.apiError(
        t("AdminExpensesManagement.ExpenseForm.Toast.CreateError"),
        error
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateExpenseData) =>
      apiClient.updateExpense(data.id.toString(), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toastHelpers.apiSuccess(
        t("AdminExpensesManagement.ExpenseForm.Toast.UpdateTitle"),
        t("AdminExpensesManagement.ExpenseForm.Toast.UpdateSuccess")
      );
      onSuccess?.();
    },
    onError: (error) => {
      toastHelpers.apiError(
        t("AdminExpensesManagement.ExpenseForm.Toast.UpdateError"),
        error
      );
    },
  });

  const onSubmit = async (data: CreateExpenseData | UpdateExpenseData) => {
    try {
      let payload = { ...data };

      // If there's a new file to upload, upload it first
      if (selectedFile) {
        setIsUploadingReceipt(true);
        try {
          const response = await apiClient.uploadImage(selectedFile);
          const receiptUrl = response.data.url;

          if (!receiptUrl) {
            throw new Error(
              t("AdminExpensesManagement.ExpenseForm.Errors.UploadFailed")
            );
          }

          // Update payload with the uploaded receipt URL
          payload = { ...payload, receipt_url: receiptUrl };

          // Clean up the blob URL and selected file
          if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
          }
          setSelectedFile(null);
          setPreviewUrl(receiptUrl);
        } catch (uploadError) {
          setIsUploadingReceipt(false);
          toastHelpers.apiError(
            t("AdminExpensesManagement.ExpenseForm.Toast.ReceiptUploadError"),
            uploadError
          );
          return; // Don't proceed with expense creation if receipt upload fails
        } finally {
          setIsUploadingReceipt(false);
        }
      }

      // Now proceed with expense creation/update
      if (isEditing) {
        updateMutation.mutate(payload as UpdateExpenseData);
      } else {
        createMutation.mutate(payload as CreateExpenseData);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toastHelpers.apiError(
        t("AdminExpensesManagement.ExpenseForm.Toast.FormSubmissionError"),
        error
      );
    }
  };

  const isPending =
    createMutation.isPending || updateMutation.isPending || isUploadingReceipt;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          {isEditing
            ? t("AdminExpensesManagement.ExpenseForm.Title.Edit")
            : t("AdminExpensesManagement.ExpenseForm.Title.Add")}
        </CardTitle>
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <TextInputField
              control={form.control}
              name="title"
              label={t("AdminExpensesManagement.ExpenseForm.Fields.Title")}
              placeholder={t(
                "AdminExpensesManagement.ExpenseForm.Fields.TitlePlaceholder"
              )}
            />
            <TextInputField
              control={form.control}
              name="description"
              label={t(
                "AdminExpensesManagement.ExpenseForm.Fields.Description"
              )}
              placeholder={t(
                "AdminExpensesManagement.ExpenseForm.Fields.DescriptionPlaceholder"
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <NumberInputField
                control={form.control}
                name="amount"
                label={t("AdminExpensesManagement.ExpenseForm.Fields.Amount")}
                step={0.01}
                min={0}
                placeholder="0.00"
              />

              <SelectField
                control={form.control}
                name="category"
                label={t("AdminExpensesManagement.ExpenseForm.Fields.Category")}
                options={categoryOptions}
              />
            </div>

            <FormField
              control={form.control}
              name="expense_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      "AdminExpensesManagement.ExpenseForm.Fields.ExpenseDate"
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("AdminExpensesManagement.ExpenseForm.Fields.Receipt")}
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                <input
                  id="receipt-upload-input"
                  type="file"
                  accept="image/*,.pdf"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-0"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    try {
                      // Store the selected file for later upload
                      setSelectedFile(file);

                      // Create local preview URL (for images only)
                      if (file.type.startsWith("image/")) {
                        const localPreviewUrl = URL.createObjectURL(file);
                        setPreviewUrl(localPreviewUrl);
                      } else {
                        setPreviewUrl(file.name);
                      }

                      // Clear the receipt_url field since we haven't uploaded yet
                      form.setValue("receipt_url", "");
                    } catch (err) {
                      console.error("File selection failed", err);
                      toastHelpers.apiError(
                        t(
                          "AdminExpensesManagement.ExpenseForm.Toast.FileSelectionError"
                        ),
                        err
                      );
                    }
                  }}
                />

                {previewUrl ? (
                  // Show preview or file name
                  <div className="text-center">
                    {previewUrl.startsWith("blob:") ||
                    previewUrl.startsWith("http") ? (
                      <div className="relative inline-block">
                        <img
                          src={previewUrl}
                          alt={t(
                            "AdminExpensesManagement.ExpenseForm.Receipt.PreviewAlt"
                          )}
                          className="h-32 w-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm mx-auto"
                        />
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm font-medium text-gray-900">
                          📄 {previewUrl}
                        </p>
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-900">
                        {selectedFile
                          ? t(
                              "AdminExpensesManagement.ExpenseForm.Receipt.ReadyForUpload"
                            )
                          : t(
                              "AdminExpensesManagement.ExpenseForm.Receipt.Uploaded"
                            )}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        {selectedFile
                          ? t(
                              "AdminExpensesManagement.ExpenseForm.Receipt.UploadOnSubmit"
                            )
                          : t(
                              "AdminExpensesManagement.ExpenseForm.Receipt.CurrentReceipt"
                            )}
                      </p>
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const fileInput = document.getElementById(
                            "receipt-upload-input"
                          ) as HTMLInputElement | null;
                          fileInput?.click();
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 hover:border-blue-300 relative z-10"
                      >
                        {t(
                          "AdminExpensesManagement.ExpenseForm.Receipt.ChangeReceipt"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Clean up blob URL if it exists
                          if (previewUrl && previewUrl.startsWith("blob:")) {
                            URL.revokeObjectURL(previewUrl);
                          }
                          // Clear the file input
                          const fileInput = document.getElementById(
                            "receipt-upload-input"
                          ) as HTMLInputElement | null;
                          if (fileInput) {
                            fileInput.value = "";
                          }
                          // Clear state
                          form.setValue("receipt_url", "");
                          setPreviewUrl("");
                          setSelectedFile(null);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 relative z-10"
                      >
                        {t(
                          "AdminExpensesManagement.ExpenseForm.Receipt.RemoveReceipt"
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Show upload prompt
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium text-gray-900 hover:text-gray-700">
                          {t(
                            "AdminExpensesManagement.ExpenseForm.Receipt.ClickToUpload"
                          )}
                        </span>{" "}
                        {t(
                          "AdminExpensesManagement.ExpenseForm.Receipt.OrDragDrop"
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t(
                          "AdminExpensesManagement.ExpenseForm.Receipt.FileTypes"
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <TextareaField
              control={form.control}
              name="notes"
              label={t("AdminExpensesManagement.ExpenseForm.Fields.Notes")}
              placeholder={t(
                "AdminExpensesManagement.ExpenseForm.Fields.NotesPlaceholder"
              )}
            />

            <div className="flex gap-2 justify-end">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isPending}
                >
                  {t("AdminExpensesManagement.ExpenseForm.Actions.Cancel")}
                </Button>
              )}
              <FormSubmitButton
                isLoading={isPending}
                loadingText={
                  isEditing
                    ? t("AdminExpensesManagement.ExpenseForm.Actions.Updating")
                    : t("AdminExpensesManagement.ExpenseForm.Actions.Creating")
                }
              >
                {isEditing
                  ? t(
                      "AdminExpensesManagement.ExpenseForm.Actions.UpdateExpense"
                    )
                  : t(
                      "AdminExpensesManagement.ExpenseForm.Actions.CreateExpense"
                    )}
              </FormSubmitButton>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
